/**
 * workspaceTabsStore — hybrid section+page workspace tabs (Context, sessionStorage).
 *
 * Tabs come in three flavours:
 *   - 'home'    → always present, non-closeable, id = 'home'.
 *   - 'section' → one per opened module (Reference Data, Market Data, ALM, …).
 *                 id = 'section:<section-id>'; label = section's label.
 *                 The label STAYS THE SAME as the user navigates between
 *                 sub-pages within the section; only `path` is updated.
 *   - 'page'    → spawned by a Cmd/Ctrl/middle-click on a sidebar leaf item
 *                 (the explicit "open in new tab" gesture).
 *                 id = 'page:<path>'; label = the leaf item's name.
 *                 path is fixed.
 *
 * Sync rules (run on every location change):
 *   1. If active tab's path matches the route → no-op.
 *   2. Else, look up the route's owning section:
 *      - If a section tab for that section already exists → updatePath + activate.
 *      - Else → open a new section tab labelled with the section's name.
 *   3. If no section owns the route, fall back to a page tab.
 *
 * Spec: 2026-05-19-qfinpricer-portal-skin-qyas.md, day-4 follow-up.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type WorkspaceTabKind = 'home' | 'section' | 'page';

export interface WorkspaceTab {
  /** Stable id. 'home' | 'section:<section-id>' | 'page:<route-path>'. */
  id: string;
  /** What kind of tab this is — drives sync behaviour. */
  kind: WorkspaceTabKind;
  /** Human-readable title shown on the tab. */
  label: string;
  /** Route this tab navigates to when activated. Tracks sub-page nav for section tabs. */
  path: string;
  /** Home is non-closeable; everything else is. */
  closeable: boolean;
  /** Optional icon name (lucide). Currently unused but kept for future surfacing. */
  icon?: string;
}

interface WorkspaceTabsState {
  tabs: WorkspaceTab[];
  activeId: string;
  /** Add a tab and activate it. No-op if a tab with the same id already exists (just activates). */
  open: (tab: WorkspaceTab) => void;
  close: (id: string) => void;
  setActive: (id: string) => void;
  /** Update the path of an existing tab (used when navigating within a tab). */
  updatePath: (id: string, path: string) => void;
  /** Update both the path and the label of an existing tab. */
  updateMeta: (id: string, patch: Partial<Pick<WorkspaceTab, 'path' | 'label'>>) => void;
  clear: () => void;
}

const STORAGE_KEY = 'qfinpricer.workspaceTabs.v3';  // v3: section/page kind split
const DEFAULT_HOME: WorkspaceTab = {
  id: 'home',
  kind: 'home',
  label: 'Home',
  path: '/',
  closeable: false,
};

const WorkspaceTabsContext = createContext<WorkspaceTabsState | null>(null);

function loadFromSession(): { tabs: WorkspaceTab[]; activeId: string } {
  if (typeof window === 'undefined') {
    return { tabs: [DEFAULT_HOME], activeId: 'home' };
  }
  // Clear stale entries from older storage versions so users don't see stale tabs
  // (e.g. UUID-labelled detail pages from the old per-page model).
  try {
    window.sessionStorage.removeItem('qfinpricer.workspaceTabs.v2');
    window.sessionStorage.removeItem('qfinpricer.workspaceTabs.v1');
  } catch {
    // ignore
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { tabs: [DEFAULT_HOME], activeId: 'home' };
    const parsed = JSON.parse(raw) as { tabs: WorkspaceTab[]; activeId: string };
    if (!Array.isArray(parsed.tabs) || parsed.tabs.length === 0) {
      return { tabs: [DEFAULT_HOME], activeId: 'home' };
    }
    // Defensive: drop any tab missing the new kind field (pre-v3 data shape).
    const sane = parsed.tabs.filter((t) => t && (t.id === 'home' || t.kind === 'section' || t.kind === 'page'));
    const hasHome = sane.some((t) => t.id === 'home');
    const tabs = hasHome ? sane : [DEFAULT_HOME, ...sane];
    return { tabs, activeId: parsed.activeId || 'home' };
  } catch {
    return { tabs: [DEFAULT_HOME], activeId: 'home' };
  }
}

function persistToSession(tabs: WorkspaceTab[], activeId: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ tabs, activeId }),
    );
  } catch {
    // Private mode / storage full — ignore.
  }
}

export const WorkspaceTabsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const initial = useMemo(() => loadFromSession(), []);
  const [tabs, setTabs] = useState<WorkspaceTab[]>(initial.tabs);
  const [activeId, setActiveId] = useState<string>(initial.activeId);

  useEffect(() => {
    persistToSession(tabs, activeId);
  }, [tabs, activeId]);

  const open = useCallback((tab: WorkspaceTab) => {
    setTabs((prev) => {
      if (prev.some((t) => t.id === tab.id)) {
        // Same id — refresh path/label so re-opening keeps the tab current.
        return prev.map((t) => (t.id === tab.id ? { ...t, path: tab.path, label: tab.label } : t));
      }
      return [...prev, tab];
    });
    setActiveId(tab.id);
  }, []);

  const close = useCallback((id: string) => {
    let fallback: string = 'home';
    setTabs((prev) => {
      const target = prev.find((t) => t.id === id);
      if (!target || !target.closeable) return prev;
      const idx = prev.findIndex((t) => t.id === id);
      const next = prev.filter((t) => t.id !== id);
      if (next.length === 0) {
        fallback = 'home';
        return [DEFAULT_HOME];
      }
      // Choose the tab to the left (or first remaining).
      fallback = next[Math.max(0, idx - 1)]?.id ?? next[0].id;
      return next;
    });
    setActiveId((curr) => (curr === id ? fallback : curr));
  }, []);

  const setActive = useCallback((id: string) => {
    setActiveId(id);
  }, []);

  const updatePath = useCallback((id: string, path: string) => {
    setTabs((prev) => prev.map((t) => (t.id === id ? { ...t, path } : t)));
  }, []);

  const updateMeta = useCallback((id: string, patch: Partial<Pick<WorkspaceTab, 'path' | 'label'>>) => {
    setTabs((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  }, []);

  const clear = useCallback(() => {
    setTabs([DEFAULT_HOME]);
    setActiveId('home');
  }, []);

  const value = useMemo<WorkspaceTabsState>(
    () => ({ tabs, activeId, open, close, setActive, updatePath, updateMeta, clear }),
    [tabs, activeId, open, close, setActive, updatePath, updateMeta, clear],
  );

  return (
    <WorkspaceTabsContext.Provider value={value}>
      {children}
    </WorkspaceTabsContext.Provider>
  );
};

export function useWorkspaceTabs(): WorkspaceTabsState {
  const ctx = useContext(WorkspaceTabsContext);
  if (!ctx) {
    throw new Error('useWorkspaceTabs must be used within <WorkspaceTabsProvider>');
  }
  return ctx;
}

/** Workspace tabs are now ON by default. Set VITE_WORKSPACE_TABS=false to hide. */
export function isWorkspaceTabsEnabled(): boolean {
  try {
    const v = (import.meta as { env?: Record<string, string> }).env?.VITE_WORKSPACE_TABS;
    return v !== 'false'; // default true unless explicitly disabled
  } catch {
    return true;
  }
}
